import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const C = { blue: '#3f51b5', yellow: '#c4d835', dark: '#2c387e', ylight: '#d4e157' };

const card = {
    background: `linear-gradient(135deg, ${C.blue}ee, ${C.dark}ee)`,
    borderRadius: '20px',
    border: `2px solid ${C.yellow}35`,
    padding: '28px',
    boxShadow: `0 8px 32px ${C.blue}22`,
    marginBottom: '20px',
    width: '100%',
    boxSizing: 'border-box',
};

const heading = { color: C.yellow, fontWeight: 700, marginBottom: '12px' };
const body = { color: C.ylight, lineHeight: 1.85 };
const ibox = {
    width: 44, height: 44, borderRadius: '12px',
    background: `${C.yellow}28`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};
const heroTitle = {
    fontWeight: 800,
    fontSize: '2.4rem',
    background: `linear-gradient(45deg, ${C.yellow}, ${C.ylight})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textAlign: 'center',
};
const primaryBtn = {
    padding: '12px 32px',
    fontWeight: 700,
    borderRadius: '12px',
    background: `linear-gradient(45deg, ${C.yellow}, ${C.ylight})`,
    color: C.dark,
    textTransform: 'none',
    fontSize: '1rem',
    border: 'none',
};

const InstagramIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z" />
    </svg>
);
const FacebookIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
);

const ContactUsPage = () => {
    const navigate = useNavigate();

    return (
        <Box sx={{ maxWidth: 880, mx: 'auto', py: 2 }}>

            {/* Hero */}
            <Box sx={{ ...card, textAlign: 'center', py: 5 }}>
                <Box sx={{ ...ibox, width: 60, height: 60, borderRadius: '50%', mx: 'auto', mb: 2 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 32, color: C.yellow }} />
                </Box>
                <Typography variant="h1" component="h1" sx={heroTitle}>
                    THE GRID BY FC ROVERS
                </Typography>
                <Typography variant="overline" sx={{ color: C.ylight, letterSpacing: '0.18em', display: 'block', mt: 1 }}>
                    Contact Us
                </Typography>
                <Typography variant="body2" sx={{ color: C.ylight, mt: 1.5, opacity: 0.85 }}>
                    Court bookings, academy inquiries, or general questions — we are here to help.
                </Typography>
            </Box>

            {/* Get In Touch */}
            <Typography variant="h6" sx={{ ...heading, mb: 2 }}>Get In Touch</Typography>
            <Box sx={{ display: 'flex', gap: '20px', mb: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* WhatsApp */}
                <Box
                    component="a"
                    href="https://wa.me/923238148153"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ ...card, mb: 0, flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}
                >
                    <Box sx={{ ...ibox, width: 54, height: 54, borderRadius: '14px' }}>
                        <PhoneIcon sx={{ fontSize: 28, color: C.yellow }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700 }}>WhatsApp</Typography>
                        <Typography variant="body2" sx={{ color: C.ylight }}>+92 323 8148153</Typography>
                        <Typography variant="caption" sx={{ color: C.ylight, opacity: 0.8 }}>Bookings and queries</Typography>
                    </Box>
                </Box>
                {/* Location */}
                <Box sx={{ ...card, mb: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ ...ibox, width: 54, height: 54, borderRadius: '14px' }}>
                        <LocationOnIcon sx={{ fontSize: 28, color: C.yellow }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700 }}>Location</Typography>
                        <Typography variant="body2" sx={{ color: C.ylight }}>Malir Cantonment, Karachi</Typography>
                        <Typography variant="caption" sx={{ color: C.ylight, opacity: 0.8 }}>The Grid — FC Rovers</Typography>
                    </Box>
                </Box>
            </Box>

            {/* Follow Us */}
            <Box sx={card}>
                <Typography variant="h6" sx={{ ...heading, textAlign: 'center', mb: 3 }}>Follow Us</Typography>
                <Box sx={{ display: 'flex', gap: '20px', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Instagram */}
                    <Box
                        component="a"
                        href="https://instagram.com/thegridbyrovers"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ ...card, mb: 0, flex: 1, textAlign: 'center', textDecoration: 'none' }}
                    >
                        <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                            <InstagramIcon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700 }}>@thegridbyrovers</Typography>
                        <Typography variant="caption" sx={{ color: C.ylight, opacity: 0.85 }}>Instagram</Typography>
                    </Box>
                    {/* Facebook */}
                    <Box
                        component="a"
                        href="https://www.facebook.com/share/1Gd8Ujvwtp/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ ...card, mb: 0, flex: 1, textAlign: 'center', textDecoration: 'none' }}
                    >
                        <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: '#1877f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                            <FacebookIcon />
                        </Box>
                        <Typography variant="subtitle2" sx={{ color: C.yellow, fontWeight: 700 }}>The Grid by Rovers</Typography>
                        <Typography variant="caption" sx={{ color: C.ylight, opacity: 0.85 }}>Facebook</Typography>
                    </Box>
                </Box>
            </Box>

            {/* CTA */}
            <Box sx={{ ...card, textAlign: 'center', py: 5 }}>
                <Typography variant="h6" sx={heading}>Ready to Play?</Typography>
                <Typography variant="body2" sx={{ ...body, mb: 3 }}>Book a court online in minutes.</Typography>
                <Button variant="contained" size="large" onClick={() => navigate('/booking')} sx={primaryBtn}>
                    Book Your Court
                </Button>
            </Box>

        </Box>
    );
};

export default ContactUsPage;
